import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faUsers,
    faStore,
    faTag,
    faFileAlt,
    faArrowRight,
    faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function AnalyticsIndex() {
    const sections = [
        {
            id: 'dashboard',
            title: 'Dashboard Analytics',
            icon: faChartLine,
            description: 'Visión analítica completa de los KPIs del negocio con visualizaciones gráficas interactivas',
            path: '/documentation/analytics/dashboard',
            status: 'disponible',
            features: [
                'Ventas totales mensuales con gráficos de barras',
                'Evolución de nuevos clientes por mes',
                'Tasa de recompra con gráficos de área',
                'Ticket promedio mensual',
                'Puntos calientes del día con selector de día de la semana',
                'Selección de marca para análisis específico'
            ]
        },
        {
            id: 'clientes',
            title: 'Estadísticas de Clientes',
            icon: faUsers,
            description: 'Análisis detallado del comportamiento y métricas de tus clientes',
            path: '/documentation/analytics/clientes',
            status: 'disponible',
            features: [
                'Vista de lista con filtros y ordenamiento',
                'Métricas detalladas por cliente individual',
                'Segmentación RFM y comportamiento',
                'Análisis de puntos acumulados y canjeados',
                'Frecuencia de visitas y valor promedio de transacción',
                'Historial completo de compras'
            ]
        },
        {
            id: 'tiendas',
            title: 'Estadísticas de Tiendas',
            icon: faStore,
            description: 'Métricas específicas de rendimiento por tienda',
            path: '/documentation/analytics/tiendas',
            status: 'disponible',
            features: [
                'Total de clientes únicos por tienda',
                'Ventas totales y tasa de conversión',
                'Valor promedio de transacción',
                'Tasa de redención de puntos',
                'Frecuencia de visitas (diaria, semanal, mensual)',
                'Productividad de trabajadores'
            ]
        },
        {
            id: 'ofertas',
            title: 'Métricas de Ofertas',
            icon: faTag,
            description: 'Análisis del rendimiento de tus ofertas promocionales',
            path: '/documentation/analytics/ofertas',
            status: 'disponible',
            features: [
                'Total de ofertas y usos',
                'Visualizaciones y tasa de conversión',
                'Top 5 ofertas más utilizadas',
                'Distribución por estado y tipo',
                'Análisis por marca',
                'Métricas detalladas de cada oferta'
            ]
        },
        {
            id: 'reporting',
            title: 'Centro de Reporting',
            icon: faFileAlt,
            description: 'Genera reportes ejecutivos profesionales con análisis avanzados',
            path: '/documentation/analytics/reporting',
            status: 'disponible',
            features: [
                'Reporte ejecutivo con métricas clave',
                'Recomendaciones de IA basadas en datos',
                'Gráficas de evolución temporal',
                'Análisis de campañas y fidelización',
                'Exportación a PDF profesional',
                'Generación asíncrona con envío por email'
            ]
        }
    ];

    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
                        <FontAwesomeIcon icon={faChartLine} className="text-blue-600 text-4xl" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Analytics y Reportes
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Analiza el rendimiento de tu negocio con métricas detalladas, gráficos interactivos y reportes profesionales que te ayudarán a tomar decisiones informadas.
                    </p>
                </div>

                {/* Introducción */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3">
                        ¿Qué es Analytics?
                    </h2>
                    <p className="text-gray-700 mb-4">
                        El módulo de Analytics te proporciona una visión completa del rendimiento de tu negocio a través de diferentes perspectivas:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li><strong>Dashboard Analytics:</strong> Visión general con KPIs clave y gráficos interactivos</li>
                        <li><strong>Estadísticas de Clientes:</strong> Análisis detallado del comportamiento y segmentación de clientes</li>
                        <li><strong>Estadísticas de Tiendas:</strong> Métricas específicas de rendimiento por ubicación</li>
                        <li><strong>Métricas de Ofertas:</strong> Análisis del rendimiento de tus campañas promocionales</li>
                        <li><strong>Centro de Reporting:</strong> Generación de reportes ejecutivos profesionales</li>
                    </ul>
                </div>

                {/* Secciones */}
                <div className="space-y-6 mb-8">
                    <h2 className="text-3xl font-bold text-gray-800 mb-6">
                        Secciones Disponibles
                    </h2>
                    {sections.map((section) => (
                        <div
                            key={section.id}
                            className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden"
                        >
                            <div className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-start space-x-4 flex-1">
                                        <div className="flex-shrink-0">
                                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                                section.status === 'disponible' 
                                                    ? 'bg-blue-100' 
                                                    : 'bg-gray-100'
                                            }`}>
                                                <FontAwesomeIcon 
                                                    icon={section.icon} 
                                                    className={`text-xl ${
                                                        section.status === 'disponible' 
                                                            ? 'text-blue-600' 
                                                            : 'text-gray-400'
                                                    }`} 
                                                />
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h3 className="text-xl font-semibold text-gray-800">
                                                    {section.title}
                                                </h3>
                                                {section.status === 'disponible' && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                                                        Disponible
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-gray-600 mb-4">
                                                {section.description}
                                            </p>
                                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                                                {section.features.map((feature, index) => (
                                                    <li key={index} className="flex items-start text-sm text-gray-700">
                                                        <span className="text-blue-500 mr-2">•</span>
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                {section.status === 'disponible' && (
                                    <Link
                                        to={section.path}
                                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors"
                                    >
                                        Ver documentación completa
                                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                                    </Link>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Tips */}
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        💡 Consejos para aprovechar Analytics
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li>Revisa el Dashboard Analytics regularmente para mantenerte al día con los KPIs principales</li>
                        <li>Utiliza las Estadísticas de Clientes para identificar oportunidades de segmentación y personalización</li>
                        <li>Compara el rendimiento entre tiendas para identificar mejores prácticas</li>
                        <li>Analiza las Métricas de Ofertas para optimizar tus campañas promocionales</li>
                        <li>Genera reportes ejecutivos mensuales para compartir con tu equipo o inversores</li>
                    </ul>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default AnalyticsIndex;

